import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleError } from '../../../../shared/functions/handleError';
import { IForgotPasswordUser } from '../../../../shared/interfaces/user/IUser';
import { AuthService } from '../../service';

const ForgotForm = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const [password, setPassword] = useState({
    password: '',
    repeatPassword: '',
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ): void => {
    const value = event.target.value;
    if (isNaN(Number(value))) return;

    let newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleBackspace = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ): void => {
    if (event.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleSubmit = async (formState: IForgotPasswordUser) => {
    await AuthService.resetPassword(formState)
      .then((response) => {
        if (response) {
          enqueueSnackbar(t('toasts.passwordResetSuccessfully'), {
            variant: 'success',
          });
          navigate('/');
        }
      })
      .catch(handleError);
  };

  const resendOtp = (): void => {
    if (!!email) {
      setIsLoading(true);
      AuthService.sendPasswordOtp(email)
        .then(() => {
          enqueueSnackbar(t('toasts.otpSent', { email }), {
            variant: 'success',
          });
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    }
  };
  return (
    <form
      style={{ width: '75%' }}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit({
          email: email || '',
          otp: Number(otp.join('')),
          ...password,
        });
      }}
    >
      <Typography variant="h4">{t('general.login.enterOtp')}</Typography>
      <Typography variant="subtitle1">
        {t('general.login.otpText')} {email}
      </Typography>
      <Box
        sx={{
          margin: '20px 0',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {otp?.map((data, index) => (
          <input
            type="text"
            key={index}
            className={'otp-input'}
            value={otp[index]}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleBackspace(e, index)}
            ref={(el) => (inputRefs.current[index] = el)}
            maxLength={1}
          />
        ))}
      </Box>

      {otp?.filter((item) => item.length)?.length === 6 ? (
        <>
          <Box
            sx={{
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TextField
              type={showPassword ? 'text' : 'password'}
              label="Password"
              onChange={(e) =>
                setPassword((prev) => ({ ...prev, password: e.target.value }))
              }
              sx={{ width: '100%' }}
            />
            <Box
              sx={{
                marginLeft: '-40px',
                position: 'relative',
                cursor: 'pointer',
              }}
            >
              {showPassword ? (
                <VisibilityOff
                  onClick={() => setShowPassword((prev) => !prev)}
                />
              ) : (
                <Visibility onClick={() => setShowPassword((prev) => !prev)} />
              )}
            </Box>
          </Box>
          <Box>
            <TextField
              type={showPassword ? 'text' : 'password'}
              label="Repeat Password"
              onChange={(e) =>
                setPassword((prev) => ({
                  ...prev,
                  repeatPassword: e.target.value,
                }))
              }
              sx={{ width: '100%' }}
            />
          </Box>
        </>
      ) : (
        ''
      )}

      <Button
        variant="contained"
        sx={{ width: '100%', marginTop: '20px' }}
        disabled={otp?.filter((item) => item.length)?.length !== 6}
        type={'submit'}
      >
        {t('button.verify')}
      </Button>

      <LoadingButton
        loading={isLoading}
        variant="outlined"
        sx={{ width: '100%', marginTop: '20px' }}
        type={'button'}
        onClick={resendOtp}
      >
        {t('button.resendOtp')}
      </LoadingButton>
    </form>
  );
};

export default ForgotForm;
